import * as angular from 'angular';

import * as d3 from 'd3';
import * as _ from 'lodash';

import { IUserSettingsService } from '../../services';
import { ITreeItem, ComponentDefinition, Group } from '../../models';
import { groupAdded, guidEmpty } from '../../common';

class GroupTreeFilterController implements angular.IComponentController {

    actualGroups: ITreeItem<Group>[];
    displayedGroups: any[];
    exposedScope: any;
    filteredGroups: ITreeItem<Group>[];
    format: any;
    groups: ITreeItem<Group>[];
    hasMore: boolean;
    isSeverityVisible: boolean;
    maxDisplayLevel: number;
    maxLevel: number;
    placeholder: string;
    searchEnable: any;
    searchString: string;
    saveName: string;

    private _configName: string;
    private _garbage: () => void;

    static $inject = ['$scope', '$rootScope', '$timeout', 'userSettingsService'];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$timeout: angular.ITimeoutService,
        private _userSettingsService: IUserSettingsService
    ) {
    }

    $onInit() {
        this.format = {
            name: f => f.uniqueName,
            group: f => f.absoluteName,
            gt: f => f['type'],
            severity: f => f.severity
        };

        if (!!this.isSeverityVisible) {
            this.placeholder = 'Search for groups (g:group c:comment gt:type s:severity)';
        } else {
            this.placeholder = 'Search for groups (g:group c:comment gt:type)';
        }

        this.searchEnable = {
            groupType: true,
            severity: !!this.isSeverityVisible,
            tag: false,
            identifier: false,
            unit: false,
            variable: false,
            source: false
        };

        this._$scope.$watchGroup(
            [
                () => this.maxDisplayLevel,
                () => this.filteredGroups
            ],
            () => {
                if (this.maxDisplayLevel && this.filteredGroups) {
                    this.filter(this.maxDisplayLevel);
                }
            }
        );

        this._$scope.$watch(() => this.searchString, (newValue, oldValue) => {
            if (newValue || oldValue) {
                this._userSettingsService.setApplicationSetting('groupTreeFilter.searchString', this.searchString);
            }
        });

        this.exposedScope = this.exposedScope || {};
        this.exposedScope.showMore = () => {
            this.maxDisplayLevel = d3.max(this.filteredGroups, f => f.depth);
        };

        this._garbage = this._$rootScope.$on(groupAdded, (e, group) => this.onGroupCreated(group));

        // restoring saved search string
        const searchString = this._userSettingsService
            .getApplicationSetting<string>('groupTreeFilter.searchString');
        if (searchString) {
            this.searchString = searchString;
        }
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['groups']?.currentValue) {
            this.actualGroups = this.groups;
            this.updateMaxLevels();
        }
    }

    $onDestroy() {
        if (this._garbage) {
            this._garbage();
        }
    }

    private updateMaxLevels() {
        if (this.actualGroups)
        {
            this.maxLevel = d3.max(this.actualGroups, g => g.depth);
            const maxDisplay = this.getSavedMaxLevel();
            if (this.maxDisplayLevel !== maxDisplay) {
                this.maxDisplayLevel = maxDisplay;
            }
        }
    }

    private onGroupCreated(group: ITreeItem<Group>) {
        let { depth } = group;
        if (depth === null || depth === undefined) {
            const parent = _.find(this.groups, g => g.guid === group.parentGUID);
            depth = parent?.depth + 1;
            group.depth = depth;
        }
        if (depth > this.maxLevel) {
            this.maxLevel = depth;
        }
        if (depth > this.maxDisplayLevel) {
            this._$timeout(() => this.maxDisplayLevel = depth);
        }
    }

    private getSavedMaxLevel(): number {
        this._configName = this.saveName ? this.saveName : 'groupTreeFilter.maxDisplayLevel';
        const savedLevel = this._userSettingsService.getApplicationSetting<number>(this._configName);
        return savedLevel ?? this.maxLevel;
    }

    private filter(maxDepth: number) {
        if (this.actualGroups) {
            const filtered = this.filteredGroups.filter(g => g.depth <= maxDepth);
            this.displayedGroups = this.rebuildHierarchy(filtered);
            this.hasMore = this.displayedGroups.length < this.filteredGroups.length;
            this._userSettingsService.setApplicationSetting(this._configName, maxDepth);
        }
    }

    // This reconstruct list to display with the group hierarchy
    // from the filtered list coming from search which contains only leaf
    private rebuildHierarchy(filtered: ITreeItem<Group>[]): ITreeItem<Group>[] {
        const guidList = [];

        filtered.forEach(f => {
            for (let current = f;
                current && (current.parent || current.guid === guidEmpty);
                current = current.parent) {
                if (guidList.indexOf(current.guid as any) < 0) {
                    guidList.push(current.guid as any);
                }
            }
        });
        const filter = this.actualGroups ? this.actualGroups.filter(o => guidList.indexOf(o.guid) >= 0) : [];
        return filter;
    }

    private buildArray(f: ITreeItem, currentList: string[], property: string): string[] {
        currentList.push(f[property]);
        if (f.children) {
            f.children.forEach(c => this.buildArray(c, currentList, property));
        }
        return currentList;
    }
}

export const wtGroupTreeFilter: ComponentDefinition = {
    componentId: 'wtGroupTreeFilter',
    controller: GroupTreeFilterController,
    template: require('./groupTreeFilter.component.html'),
    controllerAs: 'vm',
    bindings: {
        groups: '<',
        displayedGroups: '=',
        hasMore: '=',
        exposedScope: '=',
        saveName: '@?',
        isSeverityVisible: '<?'
    }
};